import { Component, OnInit, inject, ChangeDetectionStrategy, input, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';

import { EntPrimaryTableConfig, EntTablesModule } from '@ng/ent-components/ent-tables';

import { OutstandingCommentsTableConfig } from '../outstanding-requirement-form/outstanding-requirement-comments-table-config';
import { BRERequirement, ResponseStatusInterface } from 'src/app/shared/models/app.model';
import { Message, OutstandingCommentsTableRowsPerPage, ResponseStatus } from 'src/config/constants';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { AgentService } from 'src/app/shared/services/agent.service';
import { textAreaValidator } from 'src/app/shared/validators/validator';
import { OutstandingRequirementService } from 'src/app/shared/services/outstanding-requirement.service';
import { AnalyticsAppService } from 'src/app/shared/services/analytics.service';
import { FileMetadataService } from 'src/app/shared/services/file-metadata.service';
import { NgClass } from '@angular/common';
import { EntFormWidgetsModule } from '@ng/ent-components/ent-form-widgets';
import { EntUtilitiesModule } from '@ng/ent-utilities';
import { EntButtonTrayModule } from '@ng/ent-components/ent-button-tray';
import { UploadFileComponent } from '../upload-file/upload-file.component';
import { DownloadFileComponent } from '../download-file/download-file.component';


@Component({
    selector: 'app-outstanding-requirement-form',
    templateUrl: './outstanding-requirement-form.component.html',
    styleUrls: ['./outstanding-requirement-form.component.scss'],
    imports: [NgClass, EntFormWidgetsModule, FormsModule, ReactiveFormsModule, EntUtilitiesModule, EntButtonTrayModule, EntTablesModule, UploadFileComponent, DownloadFileComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutstandingRequirementFormComponent implements OnInit {
  private readonly utilService = inject(UtilsService);
  private readonly agentService = inject(AgentService);
  private readonly outstandingReqService = inject(OutstandingRequirementService);
  private readonly analytics = inject(AnalyticsAppService);
  private readonly fileMetadataService = inject(FileMetadataService);
  private readonly cdr = inject(ChangeDetectorRef);


  readonly BRERequirements = input.required<BRERequirement[]>();
  readonly outstandingCommentsTableConfig = input<EntPrimaryTableConfig>(OutstandingCommentsTableConfig);
  responseStatus: ResponseStatusInterface = ResponseStatus;
  outstandingCommentsTableCustomData: Array<any> = [];
  outstandingCommentsTableRowsPerPage: number = OutstandingCommentsTableRowsPerPage;
  message = Message;
  requirementSectionIndex: number = 0;
  confirmCompletionModalConfig = {
    header: true,
    state: false,
    footer: true,
    size: 'medium',
  };
  tableSortSelected: any = {};
  commentAddedFlagADA: any = {};
  requirementCompletedFlagADA: any = {};
  isHOUser = false;
  win: any = '';
  uploadBRERequirement!: BRERequirement;
  downloadBRERequirement!: BRERequirement;
  uploadOutstandingCommentsTableCustomData!: EntPrimaryTableConfig;
  downloadOutstandingCommentsTableCustomData!: EntPrimaryTableConfig;
  index = 0;
  isHantzReadOnly?: boolean = false;
  isBOE: boolean = false;

  ngOnInit(): void {
    this.isHOUser = this.agentService.getAgentProfile()?.isHOuser || false;
    const requirements = this.BRERequirements();
    const tableConfig = this.outstandingCommentsTableConfig();
    requirements?.sort((a, b) => a.RequirementStatus > b.RequirementStatus ? -1 : 1);
    requirements?.map((data: any, index) => {
      tableConfig.data = data.TableData;
      tableConfig.config = {...tableConfig.config};
      tableConfig.config.tableId = `outstanding-comments-${index}`;
      tableConfig.config.tableClass = `outstanding-comments-table-${index}`;
      this.outstandingCommentsTableCustomData.push({...tableConfig});
      data.textareaControl = new FormControl({value: '', disabled: data.RequirementStatus === 'Complete' || this.isHOUser}, [textAreaValidator()]);
    });
    this.isHantzReadOnly = this.agentService.getHantzReadOnly();
    this.isBOE = this.agentService.getAgentProfile()?.role === "BackOfficeAssistants";
  }

  /**
   * Sets outstanding Comments table config to display all rows
   *
   * @param index of BRERequirements object
   */
  viewAllrows(index: number): void {
    const tableConfig = this.outstandingCommentsTableConfig();
    tableConfig.config.rowsPerPage = this.outstandingCommentsTableCustomData[index].data.length;
    const tableID = this.outstandingCommentsTableCustomData[index].config.tableId;
    const tableClass = this.outstandingCommentsTableCustomData[index].config.tableClass;
    this.outstandingCommentsTableCustomData[index].config = {...tableConfig.config}
    this.outstandingCommentsTableCustomData[index].config.tableId = tableID;
    this.outstandingCommentsTableCustomData[index].config.tableClass = tableClass;
    tableConfig.config.rowsPerPage = this.outstandingCommentsTableRowsPerPage;
    if (this.tableSortSelected[index]) {
      this.outstandingCommentsTableCustomData[index].config.defaultSortOrder = 'asc';
    }
    this.utilService.setFocusOnTable(`outstanding-comments-${index}`);
  }

  /**
   * Sets outstanding Comments table config to display 3 rows
   *
   * @param index of BRERequirements object
   */
  viewLessrows(index: number): void {
    this.outstandingCommentsTableCustomData[index].config.rowsPerPage = this.outstandingCommentsTableRowsPerPage;
    this.utilService.setFocusOnTable(`outstanding-comments-${index}`);
  }

  /**
   * get sorting order when click on th of datetime in comments table
   *
   * @param e event of clicked th
   * @param i index of BRERequirement object
   */
  tableSortTriggered(e: any, i: number): void {
    this.tableSortSelected[i] = e.order;
  }

  /**
   * reset comment from error when focus out of the textarea
   *
   * @param BRERequirement object
   */
  resetError(BRERequirement: BRERequirement): void {
    BRERequirement.CommentFormError = false;
  }

  /**
   * store added comment to BRERequirement table object and AgentComments object
   *
   * @param BRERequirement object
   * @param i index of BRERequirement
   */
  addComment(BRERequirement: BRERequirement, i: number): void {
    if (BRERequirement.textareaControl?.value == '' || BRERequirement.textareaControl?.value == null || BRERequirement.textareaControl?.getError('textareaError')) {
      BRERequirement.CommentFormError = true;
      BRERequirement.textareaControl?.markAsTouched();
      return;
    }
    const commentData = {
      date:  moment(new Date()).format('MM/DD/YYYY hh:mm A'),
      userName: `${this.agentService.getAgentProfile()?.lastName?.trim()}, ${this.agentService.getAgentProfile()?.firstName?.trim()}`,
      comment: BRERequirement.textareaControl?.value?.replace(/\n/g, ' ')
    }
    BRERequirement.MsgState = {
      isError: false
    }
    this.outstandingReqService.requirementResolution('Agent', commentData, BRERequirement.ID)
      .subscribe((resp: any) => {
        if (resp === this.responseStatus.success) {
          this.addCommentsToTable(BRERequirement, commentData, i);
        } else {
          BRERequirement.MsgState = {
            isError: true
          }
          this.cdr.markForCheck();
        }
    });

    this.analytics.logAnalytics({
      event_action: 'button',
      event_name: 'add comment',
      event_type: 'pending tool - annuity',
      event_version: 'requirement'
    })
  }

  /**
   * store added comment to BRERequirement table object and AgentComments object
   *
   * @param BRERequirement object
   * @param commentData object
   * @param i index of BRERequirement
   */
  addCommentsToTable(BRERequirement: any, commentData: any, i: number): void {
    BRERequirement.TableData?.unshift({
      dateandtime: commentData.date,
      user: commentData.userName,
      comment: commentData.comment
    });
    this.utilService.getAgentComments().push({
      ID: BRERequirement.ID,
      AgentCommentText: commentData.comment,
      AgentCommentDate: commentData.date,
      AgentName: commentData.userName,
    });
    this.setMsgFlags(BRERequirement);
    this.viewLessrows(i);
    this.setADAFlag(i);
  }

  /**
   * set flags for success, warning
   *
   * @param BRERequirement object
   */
  setMsgFlags(BRERequirement: any): void {
    BRERequirement.MsgState = {
      isWarning: false
    }
    BRERequirement.MsgState = {
      isSuccess: true
    }
    setTimeout(() => {
      BRERequirement.MsgState = {
        ...BRERequirement.MsgState,
        isSuccess: false
      }
      this.cdr.markForCheck();
    }, 5000);
    BRERequirement.textareaControl?.reset();
  }

  setADAFlag(i: number): void {
    this.commentAddedFlagADA[i] = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.commentAddedFlagADA[i] = false;
      this.cdr.markForCheck();
    }, 2000);
  }

  /**
   * reset warning msg when there is no value present in textarea
   *
   * @param BRERequirement Object
   */
  textareaKeyup(BRERequirement: BRERequirement): void {
    if (!BRERequirement.textareaControl?.value) {
      BRERequirement.MsgState = {
        isWarning: false
      }
    }
  }

  /**
   * open file upload modal and set input for file upload component
   *
   * @param BRERequirement object
   * @param i index of BRERequirement
   */
  triggerUploadFileModal(BRERequirement: BRERequirement, i: number): void {
    BRERequirement.UploadFile = {
      ModalState: true,
    }
    this.index = i;
    this.uploadBRERequirement = BRERequirement;
    this.uploadOutstandingCommentsTableCustomData = this.outstandingCommentsTableCustomData[i];

    this.analytics.logAnalytics({
      event_action: 'button',
      event_name: 'upload file',
      event_type: 'pending tool - annuity',
      event_version: 'requirement'
    })
  }

  triggerDownloadFileModal(BRERequirement: BRERequirement, i: number): void {
    BRERequirement.DownloadFile = {
      ModalState: true,
    }
    this.downloadBRERequirement = BRERequirement;
    this.downloadOutstandingCommentsTableCustomData = this.outstandingCommentsTableCustomData[i];
    this.analytics.logAnalytics({
      event_action: 'button',
      event_name: 'download file modal',
      event_type: 'pending tool - annuity',
      event_version: 'requirement'
    });
  }

  getFileCount(BRERequirement: any) {
    const fileMetadata = this.fileMetadataService.getFileMetadata()
    const requirementFileMetadata = fileMetadata?.requirements?.find((file:any) => {
      return file.requirementId === BRERequirement.ID;
    })?.files;
    return requirementFileMetadata ? requirementFileMetadata?.length == 0 : true;
  }

  /**
   * Build requirement header text from the requirement type.
   * Falls back to plain "Requirement" when type is absent.
   */
  getRequirementHeader(BRERequirement: BRERequirement): string {
    const requirementType = BRERequirement?.RequirementType?.trim();
    return requirementType ? `${requirementType} Requirement` : 'Requirement';
  }
}
